// src/layout/MemoAuthWrapper.tsx
//@refresh
"use client";
import React, { useContext, useEffect, useState } from "react";
import { animationCreate } from "@/utils/utils";
import BacktoTop from "@/components/common/backToTop/BacktoTop";
import { getFirebaseAuth } from "@/config/firebase/firebaseClient";

if (typeof window !== "undefined") {
  require("bootstrap/dist/js/bootstrap");
}
import { usePathname } from "next/navigation";
import { AppContext } from "@/contextApi/AppProvider";
import { AppContextType } from "@/interFace/general";
import { useRouter } from "next/navigation";
import MemoAuthHeader from "./header/MemoAuthHeader";
import MemoAuthFooter from "./footer/MemoAuthFooter";
import { getLink, Routes } from "@/constants";
import { useTranslations } from "next-intl";
import { Translations } from "@/translations";
import { routing } from "@/i18n/routing";
import QaIssueReporter from "@/components/qa/report-issue/Reporter";
import { canAccessWebPath } from "@/lib/userInfo";

type User = any;

interface MemoAuthWrapperProps {
  children: React.ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
}

const MemoAuthWrapper: React.FC<MemoAuthWrapperProps> = ({
  children,
  hideHeader = false,
  hideFooter = false,
}) => {
  const tGeneral = useTranslations(Translations.General.Page);
  const { locale, currentUser, authResolved, userInfo, userInfoResolved, sessionRevision } = useContext(AppContext) as AppContextType;
  const [fallbackAuthResolved, setFallbackAuthResolved] = useState(false);
  const [fallbackUser, setFallbackUser] = useState<User | null>(null);
  const router = useRouter();
  const pathName = usePathname();
  const pathSegments = pathName?.split("/").filter(Boolean) ?? [];
  const pathLocale =
    pathSegments.length > 0 && routing.locales.includes(pathSegments[0] as any)
      ? pathSegments[0]
      : locale;
  const isClaimantPath = (pathName || "").startsWith(`/${pathLocale}/claimant`);
  const signInQuery = new URLSearchParams({
    callbackUrl: pathName || "/",
  });

  if (isClaimantPath) {
    signInQuery.set("claimIntent", "1");
    signInQuery.set("claimSource", "claimant-workspace");
  }

  const signInUrl = `${getLink(pathLocale, Routes.SIGN_IN)}?${signInQuery.toString()}`;
  const isIssuesReportingPage = (pathName || "").includes("/tutors/centres/issues/reporting");

  const isAuthResolved = authResolved || fallbackAuthResolved;
  const resolvedUser = authResolved ? currentUser : fallbackUser;
  const permissionsReady = userInfoResolved !== false;
  const routeAllowed = permissionsReady && canAccessWebPath(pathName || "/", userInfo);

  useEffect(() => {
    // Initialize animations following Memocipe patterns
    setTimeout(() => {
      animationCreate();
    }, 200);
  }, []);

  useEffect(() => {
    if (authResolved) {
      setFallbackAuthResolved(true);
      setFallbackUser(currentUser);
      return;
    }

    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    const resolveAuthFallback = async () => {
      try {
        const auth = await getFirebaseAuth();

        if (!isMounted) {
          return;
        }

        unsubscribe = auth.onAuthStateChanged((user) => {
          if (!isMounted) {
            return;
          }

          setFallbackUser(user);
          setFallbackAuthResolved(true);
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error("Failed to resolve auth fallback:", error);
        setFallbackUser(null);
        setFallbackAuthResolved(true);
      }
    };

    resolveAuthFallback();

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, [authResolved, currentUser]);

  useEffect(() => {
    if (!isAuthResolved || resolvedUser) {
      return;
    }

    router.replace(signInUrl);
  }, [isAuthResolved, resolvedUser, router, signInUrl]);

  useEffect(() => {
    if (isAuthResolved && resolvedUser && permissionsReady && !routeAllowed) {
      router.replace(getLink(pathLocale, Routes.PROFILE));
    }
  }, [isAuthResolved, resolvedUser, permissionsReady, routeAllowed, pathLocale, router]);

  // Show loading state while Firebase initializes
  if (!isAuthResolved) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              {tGeneral(Translations.General.InitializingMemocipe)}
            </span>
          </div>
          <p className="mt-3">{tGeneral(Translations.General.Loading)}</p>
        </div>
      </div>
    );
  }

  // Show loading state while checking authentication
  if (!resolvedUser || !routeAllowed) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              {tGeneral(Translations.General.CheckingAuthentication)}
            </span>
          </div>
          <p className="mt-3">{tGeneral(Translations.General.VerifyingCredentials)}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <BacktoTop />
      {!hideHeader && <MemoAuthHeader />}
      <main key={`${resolvedUser.uid}:${sessionRevision ?? 0}`} className="main-content">{children}</main>
      {!hideFooter && <MemoAuthFooter />}
      {!isIssuesReportingPage && <QaIssueReporter locale={pathLocale} />}
    </>
  );
};

export default MemoAuthWrapper;
